import useModalStack from '@/components/hooks/useModalStack';
import KeyHandler from '@/components/KeyHandler';
import confirmDialog from '@/lib/confirmDialog';
import setArgumentOptionSlider from '@/redux/actions/uiActions/setArgumentOptionSlider';
import submitTutorialEvent from '@/screens/workflows/redux/actions/submitTutorialEvent';
import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import BatchingSlider from './argumentOptionsSlider/BatchingSlider';
import CacheSlider from './argumentOptionsSlider/CacheSlider';
import ConditionalAuthSlider from './argumentOptionsSlider/ConditionalAuthSlider';
import ConditionSlider from './argumentOptionsSlider/ConditionSlider';
import CredentialSlider from './argumentOptionsSlider/CredentialSlider';
import EnvironmentAuthSlider from './argumentOptionsSlider/EnvironmentAuthSlider';
import FieldsSlider from './argumentOptionsSlider/FieldsSlider';
import FilterSlider from './argumentOptionsSlider/FilterSlider';
import FlowControlSlider from './argumentOptionsSlider/FlowControlSlider';
import MemoriesSlider from './argumentOptionsSlider/MemoriesSlider';
import MemorySlider from './argumentOptionsSlider/MemorySlider';
import PaginationSlider from './argumentOptionsSlider/PaginationSlider';
import RetriesSlider from './argumentOptionsSlider/RetriesSlider';
import SliceSlider from './argumentOptionsSlider/SliceSlider';
import SortSlider from './argumentOptionsSlider/SortSlider';

const ArgumentOptionSlider = ({ appsScreen = false }) => {
  const settings = useSelector((state) => state?.ui?.argumentOptionSlider);
  const hasBeenModified = useSelector((state) => state?.ui?.argumentOptionHasBeenModified);
  const open = settings?.open ?? false;
  const optionType = settings?.optionType;
  const aboveModals = settings?.aboveModals;
  const dataSource = settings?.dataSource;
  const dataSourcePath = settings?.dataSourcePath;
  const dataSavePath = settings?.dataSavePath;
  const isTransformAction = settings?.isTransformAction;
  const isTransformApp = settings?.isTransformApp;
  const isPostProcessing = settings?.isPostProcessing;
  const isConditionalAction = settings?.isConditionalAction;
  const lookupActionId = settings?.lookupActionId;
  const overrideAction = settings?.overrideAction;
  const credentialId = settings?.credentialId;
  const disabled = settings?.disabled;

  const modalIdRef = useRef('argument-option-slider');
  const calculatedZIndex = useModalStack(modalIdRef.current, open && aboveModals, 900, 10);

  const handleClose = async (shouldConfirm = false) => {
    if (!hasBeenModified || !open) {
      submitTutorialEvent('info');
      setArgumentOptionSlider({ open: false, optionType: null });
      return;
    }

    if (shouldConfirm) {
      const confirm = await confirmDialog({
        title: 'Unsaved Changes',
        message: 'You have unsaved changes that will be lost if you continue. Are you sure you want to continue?',
        confirm: 'Continue',
        cancel: 'Cancel',
        confirmVariant: 'primary'
      });

      if (confirm === true) {
        setArgumentOptionSlider({ open: false, optionType: null });
        return true;
      } else {
        return false;
      }
    }

    setArgumentOptionSlider({ open: false, optionType: null });
  };

  const keyMap = { Escape: handleClose };

  useEffect(() => {
    if (open) {
      setTimeout(() => {
        document.querySelector('.comp-argument-option-slider input')?.focus?.();
      }, 200);
    }
  }, [open]);

  const componentMap = {
    pagination: PaginationSlider,
    cache: CacheSlider,
    filter: FilterSlider,
    sortCriteria: SortSlider,
    slices: SliceSlider,
    batching: BatchingSlider,
    flowControl: FlowControlSlider,
    memory: MemorySlider,
    memories: MemoriesSlider,
    fields: FieldsSlider,
    credential: CredentialSlider,
    connectionEnvironment: EnvironmentAuthSlider,
    conditionalAuth: ConditionalAuthSlider,
    condition: ConditionSlider,
    retries: RetriesSlider
  };

  const Component = componentMap[optionType];

  return (
    <div className={`comp-argument-option-slider ${open ? 'open' : ''} ${appsScreen ? 'apps-screen' : ''} ${aboveModals ? 'above-modals' : ''}`} style={{ zIndex: calculatedZIndex }}>
      <div className={'backing'} onClick={handleClose} />
      <div className={`content ${optionType}-slider`}>
        {!!Component && (
          <Component //
            onClose={handleClose}
            disabled={disabled}
            dataSource={dataSource}
            dataSourcePath={dataSourcePath}
            dataSavePath={dataSavePath}
            isTransformAction={isTransformAction}
            isTransformApp={isTransformApp}
            isPostProcessing={isPostProcessing}
            isConditionalAction={isConditionalAction}
            lookupActionId={lookupActionId}
            overrideAction={overrideAction}
            credentialId={credentialId}
          />
        )}
      </div>
      <KeyHandler keyMap={keyMap} stopPropagation={true} global={true} />
    </div>
  );
};

export default ArgumentOptionSlider;
